import { useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_ENDPOINT = '/api/version';

interface VersionInfo {
  gitSha: string;
  version: string;
}

/**
 * Poll /api/version toutes les 5 min. Retourne `hasUpdate: true` si un
 * nouveau déploiement est détecté (gitSha différent de la valeur capturée
 * au premier appel).
 *
 * Silencieux en cas d'erreur réseau — ne casse jamais l'UI.
 */
export function useVersionCheck(apiBaseUrl: string): {
  hasUpdate: boolean;
  currentVersion: VersionInfo | null;
  newVersion: VersionInfo | null;
} {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);
  const [newVersion, setNewVersion] = useState<VersionInfo | null>(null);
  const initialSha = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchVersion = async (): Promise<VersionInfo | null> => {
      try {
        const res = await fetch(`${apiBaseUrl}${VERSION_ENDPOINT}`, {
          cache: 'no-store',
        });
        if (!res.ok) return null;
        const data = (await res.json()) as VersionInfo;
        if (!data || typeof data.gitSha !== 'string' || data.gitSha === '') {
          return null;
        }
        return data;
      } catch {
        return null;
      }
    };

    const check = async (): Promise<void> => {
      const v = await fetchVersion();
      if (!v || cancelled) return;
      if (initialSha.current === null) {
        initialSha.current = v.gitSha;
        setCurrentVersion(v);
        return;
      }
      if (v.gitSha !== initialSha.current) {
        setNewVersion(v);
        setHasUpdate(true);
      }
    };

    void check();
    const timer = setInterval(() => {
      void check();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [apiBaseUrl]);

  return { hasUpdate, currentVersion, newVersion };
}
